/**
 * Storefront Core — per-(domain, theme) customization settings (Hero / Slider design).
 *
 * The backend stores each setting as a typed, NULLABLE value and returns only the ACTIVE theme's
 * overrides inside the public domain configuration. NULL/absent means "use the theme's built-in
 * default": nothing here ever substitutes a default of its own, so an unset setting leaves the
 * existing component behavior (classes, colors, heights) exactly as it was.
 *
 * SECURITY: the values become inline styles, so they are re-validated here even though the backend
 * already validates them (and the database CHECKs them). Only an integer inside the allowed range or
 * a strict #RRGGBB color survives; everything else — rgb(), var(), url(), a style fragment, a
 * number-with-unit — is silently dropped (= theme default). Nothing user-controlled is ever
 * concatenated into a CSS string other than these two validated primitives.
 *
 * Pure module: no React, no fetching — safe on the server and the client.
 */

// Mirrors MultiDomain.Domain.Constants.DomainThemeSettingsRules (a unit test compares them).
export const HERO_DESKTOP_HEIGHT_MIN = 300;
export const HERO_DESKTOP_HEIGHT_MAX = 900;
export const HERO_MOBILE_HEIGHT_MIN = 240;
export const HERO_MOBILE_HEIGHT_MAX = 700;

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

/** True only for a whole number of pixels inside [min, max]. */
export function isValidHeight(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

/** True only for a #RRGGBB string (either case, no surrounding whitespace). */
export function isValidHexColor(value: unknown): value is string {
  return typeof value === "string" && HEX_COLOR.test(value);
}

/** The upper-cased #RRGGBB, or null when the value isn't exactly that. */
export function normalizeHexColor(value: unknown): string | null {
  return isValidHexColor(value) ? value.toUpperCase() : null;
}

/** The public form the backend sends (all optional/nullable; unknown shape → treated as no overrides). */
export type PublicThemeSettings = {
  heroDesktopHeight?: number | null;
  heroMobileHeight?: number | null;
  heroTitleColor?: string | null;
  heroDescriptionColor?: string | null;
  heroButtonBackgroundColor?: string | null;
  heroButtonTextColor?: string | null;
  /** "Default" | "Transparent" | null — see parseShowcaseBackgroundMode. */
  categoryShowcaseBackgroundMode?: string | null;
  productShowcaseBackgroundMode?: string | null;
  filterButtonBackgroundColor?: string | null;
  filterButtonTextColor?: string | null;
};

/** What the Hero component consumes: only validated overrides are present; a missing key = theme default. */
export type HeroDesign = {
  desktopHeight?: number;
  mobileHeight?: number;
  titleColor?: string;
  descriptionColor?: string;
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
};

export const NO_HERO_DESIGN: HeroDesign = Object.freeze({});

/** Drops every invalid value from a design (used by the component as a last line of defense). */
export function sanitizeHeroDesign(design: HeroDesign | null | undefined): HeroDesign {
  if (!design || typeof design !== "object") return {};

  const result: HeroDesign = {};
  if (isValidHeight(design.desktopHeight, HERO_DESKTOP_HEIGHT_MIN, HERO_DESKTOP_HEIGHT_MAX)) result.desktopHeight = design.desktopHeight;
  if (isValidHeight(design.mobileHeight, HERO_MOBILE_HEIGHT_MIN, HERO_MOBILE_HEIGHT_MAX)) result.mobileHeight = design.mobileHeight;

  const titleColor = normalizeHexColor(design.titleColor);
  if (titleColor) result.titleColor = titleColor;
  const descriptionColor = normalizeHexColor(design.descriptionColor);
  if (descriptionColor) result.descriptionColor = descriptionColor;
  const buttonBackgroundColor = normalizeHexColor(design.buttonBackgroundColor);
  if (buttonBackgroundColor) result.buttonBackgroundColor = buttonBackgroundColor;
  const buttonTextColor = normalizeHexColor(design.buttonTextColor);
  if (buttonTextColor) result.buttonTextColor = buttonTextColor;

  return result;
}

/**
 * Maps the public `themeSettings` (any shape — it comes over the network) to a HeroDesign.
 * Null/undefined/garbage → {} (no overrides, i.e. the exact existing appearance).
 */
export function resolveHeroDesign(settings: unknown): HeroDesign {
  if (!settings || typeof settings !== "object") return {};
  const s = settings as PublicThemeSettings;

  return sanitizeHeroDesign({
    desktopHeight: s.heroDesktopHeight ?? undefined,
    mobileHeight: s.heroMobileHeight ?? undefined,
    titleColor: s.heroTitleColor ?? undefined,
    descriptionColor: s.heroDescriptionColor ?? undefined,
    buttonBackgroundColor: s.heroButtonBackgroundColor ?? undefined,
    buttonTextColor: s.heroButtonTextColor ?? undefined,
  });
}

export function heroDesignHasOverrides(design: HeroDesign): boolean {
  return Object.keys(sanitizeHeroDesign(design)).length > 0;
}

// ---------------------------------------------------------------------------------------------
// Showcases (Phase 5): the OUTER section background of configured Category / Product Showcases.
//
// A closed set, never a color or class name. The backend sends the exact member name
// ("Default" | "Transparent") or null (theme default). Anything else — an unknown string, a number,
// wrong case, an object — is treated as the theme default, so a malformed response can only ever
// leave the existing look untouched. Only an exact "Transparent" changes anything.
// ---------------------------------------------------------------------------------------------

export type ShowcaseBackgroundMode = "default" | "transparent";

/** What the homepage consumes: always both values, "default" unless explicitly overridden. */
export type ShowcaseDesign = {
  readonly categoryBackground: ShowcaseBackgroundMode;
  readonly productBackground: ShowcaseBackgroundMode;
};

export const NO_SHOWCASE_DESIGN: ShowcaseDesign = Object.freeze({
  categoryBackground: "default",
  productBackground: "default",
});

/** The wire values the admin form offers (null/"" = Theme Default, persisted as NULL). */
export const SHOWCASE_BACKGROUND_WIRE_VALUES = ["Default", "Transparent"] as const;

/** Exact-name parse: unknown / null / undefined / wrong case / non-string → theme default. */
export function parseShowcaseBackgroundMode(value: unknown): ShowcaseBackgroundMode {
  return value === "Transparent" ? "transparent" : "default";
}

/** Maps the public `themeSettings` (any shape — it comes over the network) to a ShowcaseDesign. */
export function resolveShowcaseDesign(settings: unknown): ShowcaseDesign {
  if (!settings || typeof settings !== "object") return NO_SHOWCASE_DESIGN;
  const s = settings as PublicThemeSettings;

  const categoryBackground = parseShowcaseBackgroundMode(s.categoryShowcaseBackgroundMode);
  const productBackground = parseShowcaseBackgroundMode(s.productShowcaseBackgroundMode);
  if (categoryBackground === "default" && productBackground === "default") return NO_SHOWCASE_DESIGN;
  return { categoryBackground, productBackground };
}

// ---------------------------------------------------------------------------------------------
// Filters (Phase 6): THEME styling of the PRIMARY filter action only (Home "Show Products", Shop "Apply").
// Which filters exist / are shown / in which order is a DOMAIN concern (lib/filter-registry.ts) — deliberately
// not part of this per-theme object. Same rules as every other color here: strict #RRGGBB or nothing.
// ---------------------------------------------------------------------------------------------

/** Only validated overrides are present; a missing key = the theme's own button colors. */
export type FilterButtonDesign = {
  readonly backgroundColor?: string;
  readonly textColor?: string;
};

export const NO_FILTER_BUTTON_DESIGN: FilterButtonDesign = Object.freeze({});

/** Drops every invalid color (last line of defense — the values become inline styles). */
export function sanitizeFilterButtonDesign(design: FilterButtonDesign | null | undefined): FilterButtonDesign {
  if (!design || typeof design !== "object") return NO_FILTER_BUTTON_DESIGN;

  const backgroundColor = normalizeHexColor(design.backgroundColor);
  const textColor = normalizeHexColor(design.textColor);
  if (!backgroundColor && !textColor) return NO_FILTER_BUTTON_DESIGN;

  return { ...(backgroundColor ? { backgroundColor } : {}), ...(textColor ? { textColor } : {}) };
}

/** Maps the public `themeSettings` (any shape) to a FilterButtonDesign. Null/garbage → no override. */
export function resolveFilterButtonDesign(settings: unknown): FilterButtonDesign {
  if (!settings || typeof settings !== "object") return NO_FILTER_BUTTON_DESIGN;
  const s = settings as PublicThemeSettings;

  return sanitizeFilterButtonDesign({
    backgroundColor: s.filterButtonBackgroundColor ?? undefined,
    textColor: s.filterButtonTextColor ?? undefined,
  });
}

/**
 * A theme's OWN default button colors apply only when the domain configured NO override at all. As soon as an admin
 * sets either color the configured design wins untouched (a half-configured pair behaves exactly as it always did),
 * and the defaults themselves are re-validated so they can never smuggle an unsafe value into an inline style.
 */
export function applyFilterButtonDefaults(design: FilterButtonDesign, themeDefaults: FilterButtonDesign): FilterButtonDesign {
  if (design.backgroundColor || design.textColor) return design;
  return sanitizeFilterButtonDesign(themeDefaults);
}
